import _ from 'lodash';
import { DateTime } from 'luxon';

import {
  Area,
  AreaName,
  SaveProject_Input,
  useGetAllAreasQuery,
  useSaveProjectFormQuery,
  useSaveProjectMutation
} from 'generated/graphql';


import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';


export type INamedRoomArea = {
  areaId: string;
  displayName: string;
  areaName: AreaName;
};

export type IFormState = {
  activeStep: number;
  projectId: string | null;
  projectName: string;
  projectStreetOne: string;
  projectStreetTwo: string;
  locationAddressId: string;
  projectCity: string;
  projectState: string;
  projectZipCode: string;
  area2NumberOfRooms: Record<AreaName, Area & { count: number }> | null;
  actualProjectStartDate: string;
  estimatedProjectStartDate: string;
  namedAreasInProject: INamedRoomArea[];
  milestones: {
    action: string;
    estimatedStartDate: DateTime;
  }[];
  stateAbbreviation2Id: _.Dictionary<string>;
  ignoreAreaSteps: string;
};


export const initialState: IFormState = {
  activeStep: 1,
  actualProjectStartDate: DateTime.now().toFormat('yyyy-MM-dd'),
  estimatedProjectStartDate: DateTime.now().toFormat('yyyy-MM-dd'),
  projectId: '',
  projectName: '',
  area2NumberOfRooms: null,
  locationAddressId: '',
  projectStreetOne: '',
  projectStreetTwo: '',
  projectCity: '',
  projectState: '',
  projectZipCode: '',
  namedAreasInProject: [],
  milestones: [],
  ignoreAreaSteps: 'false',
  stateAbbreviation2Id: {
  }
};



export type IData = Record<AreaName, Area & { count: number }>;

export function useQuery(projectId?: string) {
  const projectTuple = useSaveProjectFormQuery({
    skip: !projectId,
    variables: {
      projectId: projectId ?? ''
    }
  });

  const areasTuple = useGetAllAreasQuery({
  });

  const form = appFormUtil.useFormState({
    initialState: {
      ...initialState
    },
    key2FieldConfig: {
      projectName: {
        label: 'Project Name',
        type: 'text'
      },
      actualProjectStartDate: {
        label: 'Actual Project Start Date',
        type: 'date'
      },
      estimatedProjectStartDate: {
        label: 'Estimated Project Start Date',
        type: 'date'
      },
      projectStreetOne: {
        label: 'Street',
        type: 'text'
      },
      projectStreetTwo: {
        label: 'Unit #',
        type: 'text'
      },
      projectCity: {
        label: 'City',
        type: 'text'
      },
      projectState: {
        label: 'State',
        type: 'text'
      },
      projectZipCode: {
        label: 'Zip Code',
        type: 'text'
      },
      ignoreAreaSteps: {
        label: 'Do not Create Rooms',
        type: 'radio'
      }
    }
  });

  const project = projectTuple.data?.project;

  useEffect(() => {
    const areas = areasTuple.data?.areas ?? [];
    const areaData = areas.reduce((acc, area) => ({
      ...acc,
      [area.name]: {
        ...area,
        count: !!projectId ? 0 : __getDefaultValues(area.name)
      }
    }), {
    }) as IData;

    if (areaData && _.isEmpty(form.formState.area2NumberOfRooms)) {
      form.dispatch({
        type: 'UPDATE',
        payload: {
          property: 'area2NumberOfRooms',
          value: areaData
        }
      });
    }

    const states = projectTuple.data?.usStates ?? [];
    if (project && project.id !== form.formState.projectId) {
      form.dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...form.formState,
          stateAbbreviation2Id: states.reduce((acc, s) => ({
            ...acc,
            [s?.abbreviation ?? '']: s?.id ?? ''
          }), {
          }),
          projectId: project.id,
          projectStreetOne: project.locationAddress.street1,
          projectStreetTwo: project.locationAddress.street2,
          projectCity: project.locationAddress.city,
          projectState: project.locationAddress.state.name,
          projectZipCode: project.locationAddress.postalCode,
          projectName: project.name,
          locationAddressId: project.locationAddress.id,
          actualProjectStartDate: (project.startDate ?? DateTime.now()).toFormat('yyyy-MM-dd')
        }
      });
    }
  }, [
    areasTuple.data?.areas,
    form,
    project,
    projectId,
    projectTuple.data?.usStates
  ]);



  return {
    form
  };
}



function __getDefaultValues(area: AreaName) {
  switch (area) {

    case 'landScape':
    case 'driveway':
    case 'walkway':
    case 'pathway':
    case 'garage':
    case 'millwork':
      return 1;
    default:
      return 0;
  }
}

export function useSaveProject() {
  const [
    fnMutate,
    tuple
  ] = useSaveProjectMutation({
  });



  const fnWrappedMutate = React.useCallback(
    async (input: SaveProject_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
